import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { listShapes } from '@gridfit/contracts/shapes'
import type { CommandShape, QueryShape } from '@gridfit/contracts/shapes'
import type { CommandResponse, ObservedBinding } from '@gridfit/contracts/wire'
import { MAX_OBSERVED } from '@gridfit/contracts/wire'
import type { JsonValue } from '@gridfit/core/canonical'
import { newUlid } from '@gridfit/edge'
import { ApiError, ResultMismatchError, describeSchemaIssues, runCommand, runQuery } from './client'
import { queryKeyFor } from './keys'
import { reportClientError } from './report'
import { stalenessFor } from './staleness'
import { CLIENT_BUILD } from '../pwa/build'
import { enqueue } from '../outbox/enqueue'

/**
 * The ONE place a `result`-schema mismatch becomes telemetry — web-pre-deploy
 * item 7's reporting half, shared by BOTH paths that carry query rows into
 * this client.
 *
 * Both call sites live in this file on purpose. A query answer arrives
 * through `runQuery`; a command answer arrives as a fold FRAGMENT written
 * straight into the cache by `commandHook`'s `onSuccess`, never touching
 * `runQuery` at all. Enforcing on one and not the other is the exact failure
 * shape this cycle already had to reopen once (the "no developer
 * identifiers" item, fixed in the entry label and missed in the synthesised
 * refusal message), so the two calls sit ten lines apart where a reader
 * fixing one cannot miss the other.
 *
 * The MESSAGE, not the `kind`, carries the classification. `kind` is a
 * CLOSED three-value enum on the server (`clientErrorBody`,
 * `packages/api/src/worker.ts`) and neither of these two failures is
 * literally a boundary catch, a `window` error or an unhandled rejection —
 * `'rejection'` is the closest of the three (a `queryFn` rejecting; a
 * mutation's post-success handler discarding a value) and is chosen over
 * adding a fourth. A fourth would be a server schema change with a real
 * rollout hazard for a purely coarse routing field: `sendErrorBeacon`
 * swallows failures by design, so a new client reporting a `kind` an
 * older worker refuses would lose every one of these reports SILENTLY —
 * the precise outcome this item exists to stop. The query name and the
 * failing field go in `message`, where they are more precise anyway and
 * where `reportClientError`'s own dedupe key (`kind + message`) makes two
 * different drifting queries two distinct reports while one query drifting
 * on every refetch stays one.
 *
 * `window.location.pathname` for `route`, matching
 * `installGlobalErrorReporting` and `ErrorBoundary` — this runs inside a
 * react-query callback with no router context to ask instead.
 */
function reportResultMismatch(queryName: string, issue: string): void {
  reportClientError(new Error(`query result did not match its declared shape: ${queryName} — ${issue}`), {
    route: window.location.pathname,
    kind: 'rejection',
  })
}

/**
 * A react-query hook for a registry query, typed from its shape (design
 * §3.3). No codegen and no generated file: `QueryShape<P, R>` already carries
 * both types, so drift between the client and the registry is a type error
 * rather than a stale artifact.
 *
 * A FACTORY that returns a hook, so `useQuery` is called inside the returned
 * closure rather than inside a `use`-prefixed function. That is safe and the
 * rules of hooks are not being bent: the returned function is only ever bound
 * to a `use`-prefixed name at module scope (`const useToyTally =
 * queryHook(toyTallyQuery)`) and called unconditionally from a component, so
 * hook order is fixed per call site exactly as it would be for a
 * hand-written hook. Worth stating because a future
 * `eslint-plugin-react-hooks` — not installed in this task — flags the shape
 * on sight: it matches on the enclosing function's NAME, which a factory's
 * anonymous return cannot have. If that plugin is added later, this call is
 * the one to allowlist, not to restructure.
 *
 * `shape.result` is handed to `runQuery` (web-pre-deploy item 7) — this is
 * the "already holds the shape" caller that makes passing the schema in
 * cleaner than a name lookup; `runQuery`'s own doc comment carries that
 * decision in full. It also deletes the `as R` this line used to end with:
 * that cast WAS the gap, a compile-time assertion about a value nothing had
 * checked, and `runQuery` now returns a genuinely parsed `R`.
 *
 * The `catch` reports and RETHROWS. Rethrowing is what lets react-query
 * surface its ordinary error state, so a screen's existing `isError` branch
 * handles a shape drift exactly as it handles a 500 — no new state for a
 * caller to learn, and no silently-blank field, which is the outcome the
 * item names.
 */
export function queryHook<P, R extends JsonValue>(
  shape: QueryShape<P, R>,
): (params: P) => UseQueryResult<R, Error> {
  const policy = stalenessFor(shape.name)
  return (params: P) =>
    useQuery({
      queryKey: queryKeyFor(shape.name, params),
      queryFn: async () => {
        try {
          return await runQuery(shape.name, params, shape.result)
        } catch (error) {
          if (error instanceof ResultMismatchError) reportResultMismatch(error.queryName, error.issue)
          throw error
        }
      },
      staleTime: policy.staleTime,
      refetchOnWindowFocus: policy.refetchOnWindowFocus,
      refetchInterval: policy.refetchInterval,
    })
}

/**
 * A react-query mutation for a registry command.
 *
 * Three things happen here that nothing else does:
 *
 *  1. **`observing`** — the currently ACTIVE queries are collected and sent
 *     with the command, so the server can compute which fold fragments this
 *     caller needs back. Capped at `MAX_OBSERVED` (32): a command that wants
 *     more is a design smell the wire schema already refuses, so the cap is
 *     applied deterministically here (sorted by key) rather than letting the
 *     request 400.
 *  2. **the registered-name filter** — react-query's cache can hold keys this
 *     registry knows nothing about: its own internal bookkeeping, or a
 *     future client-only key (Task 10 adds `['session', 'me']`). Every
 *     `observing` binding is resolved and AUTHORIZED before anything else
 *     runs (`validateObservedBindings` in `@gridfit/contracts/append`), so
 *     one unregistered name in the array earns the WHOLE command a 400 — not
 *     just that one binding dropped. Filtering here, against
 *     `listShapes().queries` (a value import, but one that carries no
 *     handlers — `shapes/types.ts` only ever calls `commandShape`/
 *     `queryShape`, never `defineCommand`/`defineQuery`), is what lets one
 *     screen mount an unrelated cache key without breaking every OTHER
 *     screen's mutations. Computed FRESH on every dispatch rather than
 *     memoised at module load: route-level code splitting means a query
 *     shape only registers itself once its OWN route's module has actually
 *     loaded, so a snapshot taken when this module first ran could miss
 *     shapes a different, later-loaded route registers.
 *  3. **fragment application** — the returned fragments are written straight
 *     into the cache under `queryKeyFor`, which is what makes read-your-writes
 *     true. Delete this and the command still succeeds while the screen shows
 *     the old figure: no error, no flake, just wrong glass.
 *
 * A REFUSAL IS NOT AN ERROR. The worker returns 200 for `refused` and
 * `abstained` deliberately, so the mutation RESOLVES with `ok: false` and the
 * caller renders the named reasons as content. Only transport and server
 * faults reject.
 *
 * A FOURTH thing happens here, for an `offline: true` shape only — the
 * outbox (design §8.2; architecture §8). `runCommand`'s `fetch` can fail two
 * genuinely different ways, and this is the one place that tells them
 * apart:
 *
 *  - `ApiError` — the server SAW this request and answered, whether with a
 *    refusal (never reaches here; see the note above), a 4xx, or a 5xx.
 *    Queueing THIS would replay a request the server already ruled on
 *    forever, or (for a genuine server fault) hammer it with retries it
 *    never asked for. Left to reject exactly as before.
 *  - anything else — `fetch` itself throwing, with NO response at all: a
 *    dropped connection, no signal, or (this file's own covering test)
 *    MSW's `HttpResponse.error()`. This IS the offline case, and the only
 *    one this branch ever queues.
 *
 * `shape.offline` gates it — `commandShape`'s own doc comment (`@gridfit/
 * contracts/shapes`) and `tools/structural/offline-scope.test.ts` are what
 * make this safe to read here without a second check: no commercial command
 * can ever carry `offline: true`, so this branch can never queue one.
 *
 * `networkMode: 'always'` (Task 20 — found by the E2E offline drill, not by
 * this file's own jsdom test): react-query's DEFAULT `networkMode`
 * (`'online'`) makes `useMutation` check its own `onlineManager` BEFORE ever
 * calling `mutationFn` — and that manager tracks a REAL `window` `online`/
 * `offline` EVENT PAIR (`@tanstack/query-core`'s own `onlineManager.ts`),
 * not `navigator.onLine` polled once. In a genuine browser, going offline
 * fires that event for real, `onlineManager.isOnline()` flips to `false`,
 * and the DEFAULT mode PAUSES the mutation — `mutationFn` above never runs,
 * so `fetch` is never called, so the `catch` block's whole "was this a
 * dropped connection?" distinction is never reached, so `enqueue` is never
 * called either. MEASURED directly (Task 20's own report has the full
 * transcript): with the default `networkMode`, clicking a real "Bump toy
 * tally" button after `context.setOffline(true)` produced ZERO network
 * activity at all — no request, no failure, nothing queued, the screen
 * simply did nothing — and adding this ONE line changed that to the
 * expected `POST .../toy.tally.bump` → `net::ERR_INTERNET_DISCONNECTED` →
 * queued, with no other code touched.
 *
 * WHY `test/outbox.test.tsx`'s OWN "queued when offline" case never caught
 * this, stated plainly rather than left as a mystery: that test simulates
 * offline by making MSW answer `http.post('/api/cmd/:command', () =>
 * HttpResponse.error())` — ONE fetch call failing — while jsdom's
 * `navigator.onLine` and `window`'s real `online`/`offline` events are
 * never touched at all. `onlineManager` therefore believes the app is
 * online throughout that entire test, the DEFAULT `networkMode` never
 * pauses anything, and `mutationFn` runs normally into the simulated
 * failure — a faithful test of "the fetch call rejects," but not of "the
 * browser is genuinely offline," which are two different states this
 * mutation's caller can be in. This is exactly the class of gap
 * `docs/frontend-testing.md`'s own "flows, not widgets" framing predicts:
 * nothing in the fast suite can fire a REAL browser network transition, so
 * nothing there could ever have found this on its own.
 *
 * SAFE for every OTHER shape too, not only `offline: true` ones: a command
 * whose shape is `offline: false` still reaches this same `mutationFn`
 * either way; the only change `networkMode: 'always'` makes is that its
 * `fetch` is genuinely ATTEMPTED while offline (surfacing a real, honest
 * error the caller's `onError`/`isError` branch already renders) rather
 * than the mutation silently sitting `isPaused: true` until connectivity
 * returns, with no feedback to the person looking at the screen in the
 * meantime — the CORRECT behaviour for a screen the user is actively
 * looking at, regardless of whether the command in question is queueable.
 */
/**
 * The queued outcome, given a shape a caller can branch on without a cast
 * (Task 18 — Task 17's own report flagged this as left undone: "`queued:
 * true` is NOT part of `CommandResponse`'s declared type ... invisible to
 * any caller typed against the function's own declared ... signature").
 *
 * `queued: true` here, `queued?: false` on the `Sent` branch below — TWO
 * literal values on the SAME property name is what makes this a real
 * discriminated union TypeScript can narrow on, rather than an inert extra
 * field: `if (result.data?.queued)` now narrows `result.data` to
 * `QueuedCommandOutcome` in the `if`, and to `Sent` (so ordinary
 * `CommandResponse` fields — `.ok`, `.events`, `.failures` — are all still
 * reachable) in the `else`. `queued?: false` costs nothing at the call
 * site that builds an ordinary `CommandResponse` (`return await
 * runCommand(...)` below): an object with no `queued` property at all
 * already satisfies an OPTIONAL field typed `false`, so that return
 * statement needed no change to keep compiling.
 */
type Sent = CommandResponse & { readonly queued?: false }
export type QueuedCommandOutcome = {
  readonly ok: true
  readonly queued: true
  readonly events: readonly []
  readonly fragments: readonly []
}
export type CommandOutcome = Sent | QueuedCommandOutcome

export function commandHook<P>(
  shape: CommandShape<P>,
): () => UseMutationResult<CommandOutcome, Error, P> {
  return () => {
    const client = useQueryClient()

    return useMutation({
      mutationFn: async (payload: P) => {
        const registeredQueryNames = new Set(listShapes().queries.map((query) => query.name))

        const observing: ObservedBinding[] = client
          .getQueryCache()
          .findAll({ type: 'active' })
          .map((entry) => entry.queryKey)
          .filter(
            (key): key is [string, string] =>
              Array.isArray(key) &&
              typeof key[0] === 'string' &&
              typeof key[1] === 'string' &&
              registeredQueryNames.has(key[0]),
          )
          .map((key) => ({ query: key[0], params: JSON.parse(key[1]) as unknown }))
          .sort((a, b) => (a.query < b.query ? -1 : 1))
          .slice(0, MAX_OBSERVED)

        // Minted ONCE, here, and reused verbatim if this command ends up
        // queued below — never re-minted at drain (`enqueue.ts`/`drain.ts`
        // both carry the full reasoning; `test/outbox.test.tsx`'s "draining
        // the same entry twice appends one event" is what this protects).
        const idempotencyKey = newUlid()

        try {
          return await runCommand(shape.name, {
            idempotencyKey,
            clientBuild: CLIENT_BUILD,
            payload,
            observing,
          })
        } catch (error) {
          if (shape.offline && !(error instanceof ApiError)) {
            await enqueue({
              idempotencyKey,
              command: shape.name,
              payload,
              clientBuild: CLIENT_BUILD,
              enqueuedAt: new Date().toISOString(),
              status: 'queued',
            })
            // Resolves rather than rejects — this file's own "a refusal is
            // not an error" precedent, extended: a write that is safely
            // durable on this device and will reach the server once
            // `startOutboxAutoDrain` (`main.tsx`) next fires is even less of
            // one. A `QueuedCommandOutcome` (Task 18 — see this file's own
            // `CommandOutcome` doc comment above): `queued: true` is now
            // part of `commandHook`'s DECLARED return type, so a caller can
            // branch on `result.data?.queued` and get `QueuedCommandOutcome`
            // narrowed in the `if` with no cast — Task 18's Sync tray reads
            // this (`BumpScreen`, `test/outbox.test.tsx`, already does, via
            // a structural narrowing rather than a cast, since before this
            // task the field existed on the VALUE but not the TYPE).
            //
            // A KNOWN INTERIM LIE, recorded rather than hidden (review
            // round 1, narrowed by Task 18 rather than closed outright):
            // `ok: true` with zero events is, read literally, "nothing
            // happened, no error" — a screen that checks ONLY `.ok`, never
            // `.queued`, still renders unqualified success for a write that
            // has NOT yet reached the server. The type now makes that
            // omission a CHOICE a caller can see and skip past
            // (`result.data.queued` is a real, typed property, not a cast
            // away a lint rule would have to invent), not a fact nothing
            // could even name — this task's brief is explicit that
            // resolving, not rejecting, is correct either way.
            return { ok: true, events: [], fragments: [], queued: true }
          }
          throw error
        }
      },

      /**
       * THE SECOND HALF of web-pre-deploy item 7. A fragment carries query
       * ROWS — the same contract `runQuery` now validates — but it arrives
       * on the COMMAND response and is written straight into the cache here,
       * bypassing `runQuery` entirely. Validating only there would have
       * shipped the item half-built.
       *
       * The name→shape map is built FRESH on every dispatch, for the SAME
       * reason `mutationFn`'s `registeredQueryNames` above is (see point 2
       * of this function's doc comment): route-level code splitting means a
       * query shape only registers itself once its own route's module has
       * loaded, so a map memoised when THIS module first ran could miss a
       * shape a later-loaded route registers, and would then reject a
       * perfectly good fragment for a query it simply had not heard of yet.
       *
       * A FAILING FRAGMENT DOES NOT FAIL THE MUTATION. The command
       * SUCCEEDED: the server authorized it, the ledger has its events, and
       * `response.ok` says so. Rejecting the mutation here would tell the
       * person at the screen their write did not land when it did — a lie
       * about what happened on the server, and a worse one for an `offline:
       * true` shape, where a caller that treats a rejection as "not sent"
       * could reasonably try to send it again. So the honest shape is the
       * narrow one: refuse to write a row this client cannot trust, report
       * it, and let the mutation resolve as the success it was. The cache
       * keeps its previous, valid value and the query's own next fetch —
       * through `runQuery`, now validating — is what surfaces the drift to
       * the user if it persists.
       *
       * DELIBERATELY NOT `invalidateQueries` on a failure. Refetching would
       * ask the SAME server for the SAME fold that just produced the bad
       * rows, so it would almost certainly fail the same parse — trading a
       * stale-but-valid figure for a broken screen, immediately, on a drift
       * the user can do nothing about. The staleness policy already
       * re-asks on its own schedule.
       *
       * PER FRAGMENT, not all-or-nothing: one drifting query does not stop a
       * second, unrelated fragment in the same response from refreshing the
       * screen it belongs to.
       *
       * An UNREGISTERED name is treated as a failure to validate, not as
       * permission to skip validating. It should be unreachable — the server
       * computes fragments only for the `observing` bindings this same
       * dispatch sent, and those were already filtered against
       * `listShapes()` — and the set of registered shapes only ever GROWS
       * between `mutationFn` and here. Reaching it would mean the server
       * invented a fragment, which is the last moment to start trusting one.
       */
      onSuccess: (response) => {
        const shapesByName = new Map(listShapes().queries.map((query) => [query.name, query] as const))
        for (const fragment of response.fragments) {
          const shape = shapesByName.get(fragment.query)
          if (!shape) {
            reportResultMismatch(fragment.query, 'no query shape is registered under this name')
            continue
          }
          const parsed = shape.result.safeParse(fragment.rows)
          if (!parsed.success) {
            reportResultMismatch(fragment.query, describeSchemaIssues(parsed.error))
            continue
          }
          client.setQueryData(queryKeyFor(fragment.query, fragment.params), parsed.data)
        }
      },
      networkMode: 'always',
    })
  }
}
